import React, { useMemo } from "react";
import {
  Box,
  IconButton,
  Typography,
  Avatar,
  Tooltip,
  useTheme,
  Chip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import SendIcon from "@mui/icons-material/Send";
import ImageIcon from "@mui/icons-material/Image";
import AttachFileIcon from "@mui/icons-material/AttachFile";

import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { usePageContext } from "../hooks/usePageContext";
import { usePageActions } from "../context/PageActionsContext";
import { HeaderButton, NoticeboardButton } from "./shared";
import AskNoticeboardButton from "./AskNoticeboard/AskNoticeboardButton";
import { PLANNERS } from "../routes";

const getDashboardPrompt = () => "Send a notice";

const TopNavBar: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const pageContext = usePageContext();
  const { actions } = usePageActions();
  const hideEnhancedHeaderIcon = pageContext.title === "Create Notice";

  const avatarUrl =
    typeof user?.schoolLogo === "string" && user.schoolLogo.trim().length > 0
      ? user.schoolLogo
      : undefined;

  const branchName =
    process.env.REACT_APP_BRANCH ||
    process.env.REACT_APP_GIT_BRANCH ||
    process.env.REACT_APP_VERCEL_GIT_COMMIT_REF ||
    "";
  const branchLabel = branchName.trim() || null;
  const showBranchLabel = branchLabel && process.env.NODE_ENV !== "production";

  const userInitials = useMemo(() => {
    const name: string =
      (user?.fullName as string) || (user?.schoolName as string) || "User";
    return name
      .split(" ")
      .map((p: string) => p.charAt(0).toUpperCase())
      .slice(0, 2)
      .join("");
  }, [user?.fullName, user?.schoolName]);
  const isDashboardHome =
    location.pathname === "/" || location.pathname === "/dashboard";

  const isWorksheetsHub = location.pathname.startsWith("/create/worksheets");
  const isResourcesHub =
    location.pathname === "/resources" || location.pathname.startsWith("/resources/");
  const isLibraryHub = location.pathname === "/library";
  const isPlannerPage =
    location.pathname === PLANNERS || location.pathname.startsWith(`${PLANNERS}/`);

  // Hide top header completely on selected full-bleed pages (hub templates).
  if (
    location.pathname === "/create-notice" ||
    location.pathname === "/help/videos" ||
    isLibraryHub ||
    isPlannerPage ||
    isWorksheetsHub ||
    isResourcesHub
  ) {
    return null;
  }

  // If this page doesn't need a header, show the original simple nav
  if (!pageContext.isPageWithHeader) {
    return (
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: theme.zIndex.appBar,
          backgroundColor: theme.palette.background.paper,
          borderBottom: "1px solid",
          borderColor: "grey.300",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1.5,
          py: 0.8,
          mb: 1.5,
          gap: 1.25,
          flexWrap: isDashboardHome ? "nowrap" : "wrap",
        }}
      >
        {isDashboardHome && (
          <Box
            sx={{
              flex: { md: "1 1 auto" },
              width: "100%",
              minWidth: 0,
              maxWidth: { md: 1120 },
              mr: { md: 1.25 },
              display: { xs: "none", md: "grid" },
              gridTemplateColumns: "minmax(0, 1fr) auto auto",
              gap: 0.55,
              alignItems: "center",
              bgcolor: "common.white",
              border: "1px solid",
              borderColor: alpha(theme.palette.primary.main, 0.12),
              borderRadius: 999,
              px: 0.7,
              py: 0.35,
              boxShadow: "0 6px 16px rgba(15, 23, 42, 0.05)",
            }}
          >
            <Box
              onClick={actions.onOpenInfoNotice}
              sx={{
                minWidth: 0,
                minHeight: 38,
                borderRadius: 999,
                px: 1.35,
                display: "flex",
                alignItems: "center",
                bgcolor: "#eef1f7",
                color: "#5b6472",
                cursor: "pointer",
                "&:hover": { bgcolor: "#e8edf5" },
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.9rem",
                  lineHeight: 1.1,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {getDashboardPrompt()}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.45 }}>
              {[
                {
                  key: "send-notice",
                  icon: <SendIcon sx={{ fontSize: 17 }} />,
                  color: theme.palette.primary.main,
                  bg: alpha(theme.palette.primary.main, 0.14),
                  onClick: actions.onOpenInfoNotice,
                },
                {
                  key: "send-image",
                  icon: <ImageIcon sx={{ fontSize: 17 }} />,
                  color: "#fb7185",
                  bg: "rgba(251, 113, 133, 0.12)",
                  onClick: () => actions.onOpenSendImage?.(),
                },
                {
                  key: "send-attachment",
                  icon: <AttachFileIcon sx={{ fontSize: 17 }} />,
                  color: "#f59e0b",
                  bg: "rgba(245, 158, 11, 0.14)",
                  onClick: () => actions.onOpenSendAttachment?.(),
                },
              ].map((item) => (
                <IconButton
                  key={item.key}
                  onClick={() => item.onClick?.()}
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: 2,
                    color: item.color,
                    bgcolor: item.bg,
                    "&:hover": { bgcolor: item.bg },
                  }}
                >
                  {item.icon}
                </IconButton>
              ))}
            </Box>
          </Box>
        )}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          {showBranchLabel && (
            <Chip
              label={`${branchLabel}`}
              size="small"
              sx={{
                bgcolor: "red",
                color: theme.palette.primary.contrastText,
                fontWeight: 600,
              }}
            />
          )}
          <AskNoticeboardButton />
          <Tooltip title="Account">
            <IconButton
              size="small"
              onClick={() => navigate("/profile")}
              aria-label="Open profile"
              sx={{ p: 0 }}
              data-testid="top-nav-profile-button"
            >
              <Avatar
                src={avatarUrl}
                key={user?.schoolLogo} // Force re-render when logo changes
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: avatarUrl ? "transparent" : "primary.main",
                  fontSize: (theme) => theme.typography.body2.fontSize,
                }}
              >
                {!avatarUrl && userInitials}
              </Avatar>
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    );
  }

  // Enhanced navigation with page context
  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: theme.zIndex.appBar,
        border: "1px solid",
        borderColor: alpha(theme.palette.primary.main, 0.14),
        background: `radial-gradient(circle at 85% 15%, ${alpha(
          theme.palette.primary.main,
          0.12
        )} 0%, transparent 45%), linear-gradient(135deg, ${theme.palette.gradients.heroSurface[0]} 0%, ${theme.palette.gradients.heroSurface[1]} 100%)`,
        borderRadius: { xs: 2, md: 3 },
        padding: { xs: 2, md: 2.5 },
        marginBottom: 3,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: { xs: "wrap", md: "nowrap" },
          gap: 2,
        }}
      >
        {/* Left side - Icon, Title and Subtitle */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: { xs: "flex-start", md: "center" },
              flexDirection: { xs: "column", md: "row" },
              gap: { xs: 1, md: 2 },
            }}
          >
            {/* Icon */}
            {pageContext.icon && !hideEnhancedHeaderIcon && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: ["Notices", "Calendar", "Drive", "Team", "Circles", "Users", "Account"].includes(pageContext.title || "") ? "transparent" : alpha(theme.palette.primary.main, 0.12),
                  borderRadius: ["Notices", "Calendar", "Drive", "Team", "Circles", "Users", "Account"].includes(pageContext.title || "") ? 0 : 2,
                  width: 48,
                  height: 48,
                  flexShrink: 0,
                  color: "primary.main",
                }}
              >
                {pageContext.icon}
              </Box>
            )}

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 0,
                }}
              >
                <Typography
                  variant="h4"
                  component="h1"
                  sx={{
                    fontWeight: 400,
                    fontSize: { xs: "1.25rem", md: "1.625rem" },
                    color: "text.primary",
                  }}
                >
                  {pageContext.title}
                </Typography>
                {typeof actions.count === "number" && (
                  <Chip
                    label={actions.count.toLocaleString()}
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.12),
                      color: "primary.main",
                      fontWeight: 600,
                      fontSize: (theme) => theme.typography.body2.fontSize,
                    }}
                  />
                )}
              </Box>
              {pageContext.subtitle && (
                <Typography
                  variant="body1"
                  sx={{
                    color: "text.secondary",
                    fontSize: { xs: "0.75rem", md: "0.875rem" },
                    maxWidth: { xs: "100%", md: "80%" },
                    mt: 0.5,
                  }}
                >
                  {pageContext.subtitle}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        {/* Right side - Actions */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexShrink: 0,
          }}
        >
          {showBranchLabel && (
            <Chip
              label={`${branchLabel}`}
              size="small"
              sx={{
                bgcolor: "red",
                color: "common.white",
                fontWeight: 600,
              }}
            />
          )}
          <AskNoticeboardButton />
          {/* Sync Button */}
          {pageContext.showSync && (
            <NoticeboardButton
              variant="secondary"
              size="small"
              onClick={() => {
                if (actions.onSync) {
                  actions.onSync();
                } else {
                  window.location.reload();
                }
              }}
              data-testid="top-nav-sync-button"
              sx={{
                borderColor: alpha(theme.palette.primary.main, 0.35),
                color: "primary.main",
                bgcolor: alpha(theme.palette.primary.main, 0.06),
                textShadow: "none",
                fontSize: (theme) => theme.typography.body2.fontSize,
                transition: "all 0.3s ease",
                "&:hover": {
                  borderColor: "primary.main",
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  transform: "translateY(-2px)",
                },
              }}
            >
              SYNC
            </NoticeboardButton>
          )}

          {((pageContext.buttonText && actions.onMainAction) ||
            actions.secondaryAction ||
            actions.onVideoPlay) && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.25,
                flexShrink: 0,
              }}
            >
              {actions.secondaryAction && (
                <HeaderButton
                  onClick={actions.secondaryAction.onClick}
                  data-testid="top-nav-secondary-action-button"
                  sx={{ mr: 1 }}
                >
                  {actions.secondaryAction.label}
                </HeaderButton>
              )}
              {pageContext.buttonText && actions.onMainAction && (
                <HeaderButton
                  onClick={() => actions.onMainAction?.()}
                  data-testid="top-nav-main-action-button"
                >
                  {pageContext.buttonText}
                </HeaderButton>
              )}

              {actions.onVideoPlay && (
                <Tooltip title="Watch tutorial">
                  <IconButton
                    onClick={actions.onVideoPlay}
                    sx={{
                      color: "primary.main",
                      width: 36,
                      height: 36,
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                      },
                    }}
                    data-testid="top-nav-play-button"
                    aria-label="Watch tutorial"
                  >
                    <PlayCircleIcon sx={{ fontSize: 34 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default TopNavBar;
